import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Blocks,
  Clock3,
  FileText,
  KeyRound,
  Moon,
  Palette,
  Settings2,
  Sun,
  UserRound,
  Wallet,
} from "lucide-react";
import swal from "../../utils/swal";
import { useTheme } from "../../hooks/useTheme";
import { MODULE_LINKS, MODULE_META, useGetAppInfo, useGetModules, useUpdateAppInfo, useUpdateModules } from "../../query/settingsQuery";
import TeacherAttendancePanel from "./teacherAttendance";
import FeePolicyPanel from "./feePolicy";
import "./settings.css";

const SECTIONS = [
  { id: "modules", label: "App modules", icon: Blocks, hint: "Show or hide optional app features" },
  { id: "fees", label: "Fee policy", icon: Wallet, hint: "Due dates, late fees, and payment instructions" },
  { id: "about", label: "App information", icon: FileText, hint: "Version, privacy policy, and terms" },
  { id: "teachers", label: "Teacher attendance", icon: Clock3, hint: "Times, leave, and open weekends" },
  { id: "appearance", label: "Appearance", icon: Palette, hint: "Admin panel look and feel" },
  { id: "account", label: "Account", icon: UserRound, hint: "Profile and password" },
];

function Toggle({ on, onClick, disabled, label }) {
  return (
    <button
      type="button"
      className={`settings-toggle${on ? " is-on" : ""}`}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      aria-label={label}
    >
      <span className="settings-toggle-thumb" />
    </button>
  );
}

function ModulesSkeleton() {
  return (
    <div className="settings-panel-body" aria-busy="true" aria-label="Loading modules">
      <div className="settings-panel-intro">
        <div className="settings-skel-copy">
          <span className="settings-skel-line is-title" />
          <span className="settings-skel-line" />
          <span className="settings-skel-line is-short" />
        </div>
        <span className="settings-skel-chip" />
      </div>
      <div className="settings-module-grid">
        {MODULE_META.map((item) => (
          <div className="settings-module-card is-skeleton" key={item.key}>
            <div className="settings-skel-copy">
              <span className="settings-skel-line is-title" />
              <span className="settings-skel-line" />
            </div>
            <span className="settings-skel-toggle" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ModulesPanel() {
  const { data: modules, isLoading, isError } = useGetModules();
  const { mutate: updateModules, isPending } = useUpdateModules();

  const enabledCount = useMemo(() => {
    if (!modules) return 0;
    return MODULE_META.filter((item) => modules[item.key] !== false).length;
  }, [modules]);

  const toggle = (key) => {
    if (!modules) return;
    const value = modules[key] === false;
    const next = { ...modules, [key]: value };
    const pair = MODULE_LINKS[key];
    if (pair) next[pair] = value;
    const linkedLabel = MODULE_META.find((item) => item.key === pair)?.label;
    updateModules(next, {
      onSuccess: () =>
        swal(
          "Saved",
          linkedLabel
            ? `${MODULE_META.find((item) => item.key === key)?.label} and ${linkedLabel} were updated together`
            : "App modules updated",
          "success"
        ),
      onError: (error) => swal("Error", error?.message || "Could not save", "error"),
    });
  };

  if (isLoading) {
    return <ModulesSkeleton />;
  }

  if (isError || !modules) {
    return <p className="settings-muted">Could not load app modules. Refresh and try again.</p>;
  }

  return (
    <div className="settings-panel-body">
      <div className="settings-panel-intro">
        <div>
          <h2>App modules</h2>
          <p>
            Turn optional parent and teacher features on or off. Results and Tests
            stay together: closing one closes the other. Attendance, classes, posts,
            and support always stay available.
          </p>
        </div>
        <span className="settings-chip">
          {enabledCount}/{MODULE_META.length} on
        </span>
      </div>

      <div className="settings-module-grid">
        {MODULE_META.map((item) => {
          const on = modules[item.key] !== false;
          return (
            <div className={`settings-module-card${on ? " is-on" : ""}`} key={item.key}>
              <div className="settings-module-copy">
                <div className="settings-module-title-row">
                  <strong>{item.label}</strong>
                  <span className={`settings-status${on ? " is-on" : ""}`}>
                    {on ? "Enabled" : "Hidden"}
                  </span>
                </div>
                <span>{item.hint}</span>
                <span className="settings-module-tags">
                  <em>{item.audience}</em>
                  {item.linked ? <em>Linked with {item.linked}</em> : null}
                </span>
              </div>
              <Toggle
                on={on}
                disabled={isPending}
                label={`Toggle ${item.label}`}
                onClick={() => toggle(item.key)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EMPTY_APP_INFO = {
  teacherVersion: "",
  parentVersion: "",
  privacyPolicy: "",
  termsOfUse: "",
};

function AppInfoPanel() {
  const { data, isLoading, isError } = useGetAppInfo();
  const { mutate: save, isPending } = useUpdateAppInfo();
  const [form, setForm] = useState(EMPTY_APP_INFO);

  useEffect(() => {
    if (data) setForm({ ...EMPTY_APP_INFO, ...data });
  }, [data]);

  const setField = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const onSave = () => {
    save(form, {
      onSuccess: () => swal("Saved", "Parent and teacher apps will show this information", "success"),
      onError: (error) => swal("Error", error?.message || "Could not save", "error"),
    });
  };

  if (isLoading) {
    return <p className="settings-muted">Loading app information…</p>;
  }

  if (isError) {
    return <p className="settings-muted">Could not load app information. Refresh and try again.</p>;
  }

  return (
    <div className="settings-panel-body">
      <div className="settings-panel-intro">
        <div>
          <h2>App information</h2>
          <p>
            These appear in Settings on the parent and teacher apps. Leave a field
            blank until the school is ready to publish it.
          </p>
        </div>
      </div>
      <div className="settings-app-form">
        <label>
          Teacher app version
          <input value={form.teacherVersion} onChange={setField("teacherVersion")} maxLength={40} placeholder="1.0.0" />
        </label>
        <label>
          Parent app version
          <input value={form.parentVersion} onChange={setField("parentVersion")} maxLength={40} placeholder="1.0.0" />
        </label>
        <label className="settings-app-wide">
          Privacy policy
          <textarea value={form.privacyPolicy} onChange={setField("privacyPolicy")} rows={8} placeholder="Write the privacy policy parents and teachers will read" />
        </label>
        <label className="settings-app-wide">
          Terms of use
          <textarea value={form.termsOfUse} onChange={setField("termsOfUse")} rows={8} placeholder="Write the terms of use parents and teachers will read" />
        </label>
      </div>
      <button type="button" className="settings-save" onClick={onSave} disabled={isPending}>
        {isPending ? "Saving…" : "Save app information"}
      </button>
    </div>
  );
}

function AppearancePanel() {
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="settings-panel-body">
      <div className="settings-panel-intro">
        <div>
          <h2>Appearance</h2>
          <p>Personal display settings for this admin account.</p>
        </div>
      </div>

      <div className="settings-pref-list">
        <div className="settings-pref-row">
          <div className="settings-pref-copy">
            <strong>Dark mode</strong>
            <span>Use a darker theme across the admin panel</span>
          </div>
          <Toggle
            on={isDark}
            onClick={toggleTheme}
            label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          />
        </div>
        <div className="settings-pref-row">
          <div className="settings-pref-copy">
            <strong>Current theme</strong>
            <span>{isDark ? "Dark mode is on" : "Light mode is on"}</span>
          </div>
          <span className="settings-theme-icon">
            {isDark ? <Moon size={18} /> : <Sun size={18} />}
          </span>
        </div>
      </div>
    </div>
  );
}

function AccountPanel() {
  const navigate = useNavigate();

  return (
    <div className="settings-panel-body">
      <div className="settings-panel-intro">
        <div>
          <h2>Account</h2>
          <p>Manage your admin profile and sign-in security.</p>
        </div>
      </div>

      <div className="settings-link-grid">
        <button type="button" className="settings-link-card" onClick={() => navigate("/profile")}>
          <span className="settings-link-icon">
            <UserRound size={18} />
          </span>
          <span className="settings-link-copy">
            <strong>My profile</strong>
            <em>Name, email, and photo</em>
          </span>
        </button>
        <button
          type="button"
          className="settings-link-card"
          onClick={() => navigate("/profile/changePass")}
        >
          <span className="settings-link-icon">
            <KeyRound size={18} />
          </span>
          <span className="settings-link-copy">
            <strong>Change password</strong>
            <em>Update your admin password</em>
          </span>
        </button>
      </div>
    </div>
  );
}

export default function Settings() {
  const [params] = useSearchParams();
  const requested = params.get("section");
  const [section, setSection] = useState(
    SECTIONS.some((item) => item.id === requested) ? requested : "modules"
  );
  const active = SECTIONS.find((item) => item.id === section) || SECTIONS[0];

  return (
    <div className="settings-page">
      <header className="settings-hero">
        <div className="settings-hero-icon">
          <Settings2 size={22} />
        </div>
        <div>
          <h1>Settings</h1>
          <p>Configure school app modules and admin preferences.</p>
        </div>
      </header>

      <div className="settings-layout">
        <nav className="settings-nav" aria-label="Settings sections">
          {SECTIONS.map((item) => {
            const Icon = item.icon;
            const on = item.id === section;
            return (
              <button
                key={item.id}
                type="button"
                className={`settings-nav-item${on ? " is-active" : ""}`}
                onClick={() => setSection(item.id)}
              >
                <Icon size={16} strokeWidth={1.9} />
                <span>
                  <strong>{item.label}</strong>
                  <em>{item.hint}</em>
                </span>
              </button>
            );
          })}
        </nav>

        <section className="settings-panel" aria-label={active.label}>
          {section === "modules" ? <ModulesPanel /> : null}
          {section === "fees" ? <FeePolicyPanel /> : null}
          {section === "about" ? <AppInfoPanel /> : null}
          {section === "teachers" ? <TeacherAttendancePanel /> : null}
          {section === "appearance" ? <AppearancePanel /> : null}
          {section === "account" ? <AccountPanel /> : null}
        </section>
      </div>
    </div>
  );
}
